<script setup>
/**
 * EmployerBasicInfoForm - A reusable component for displaying and handling employer basic information forms
 * 
 * This component can be used in both the HomePage modal and the RenewEmployerModal
 * with different configurations.
 * 
 * @prop {Object} employerData - The employer data object (initial values)
 * @prop {Boolean} hideEmployerName - Whether to hide the employer name field
 * 
 * @emits {update} - Emitted when a field is updated with the new values and validation state
 */
import { ref, defineProps, defineEmits, watch, inject } from 'vue'
import { vMaska } from 'maska'
import { useForm } from 'vee-validate'
import { toTypedSchema } from '@vee-validate/yup'
import { object, string, date, array } from 'yup'
import BcsDynamicText from '../BcsDynamicText.vue'
import BcsDropdownSelect from './BcsDropdownSelect.vue'
import { get } from 'lodash'
import { getAuth } from 'firebase/auth'
import { RequestService } from '../../services/RequestService'
import { getExcludedDomains, isExcludedEmail } from '../../utils/excludedDomains'
import { getAccountOwners, normalizeOwnerEmail } from '../../utils/accountOwnersHelpers'

const MAX_ACCOUNT_OWNERS = 3

const functionsBaseUrl = inject('functionsBaseUrl')
const { handleErrors, simpleGet } = RequestService()

const props = defineProps({
  // The employer data object with initial values
  employerData: {
    type: Object,
    required: true
  },
  // Is employer duplicated
  checkDuplicateEmployer: {
    type: Boolean,
    default: false
  },
  // Duplicate employer match
  duplicateEmployerMatch: {
    type: Object,
    default: () => null
  },
  forceUpdate: {
    type: Boolean,
    default: false,
    required: false
  },
  // Function to check if the name is taken
  isNameTaken: {
    type: Function,
    default: () => false
  },
  // Function to navigate to the existing employer
  goToExistingEmployer: {
    type: Function,
    default: () => null
  },
  // Function to continue editing employer
  continueEditEmployer: {
    type: Function,
    default: () => null
  },
  lastPlanStartDate: {
    type: String,
    required: false
  },
  isEditMode: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update', 'nameChange'])
const emitter = inject('emitter')

// Raw mask value for input masking
const rawMaskVal = ref({})

// Account Owners state
const accountOwnerOptions = ref([])
const loadingAccountOwners = ref(false)
// Whether the logged-in user belongs to a domain listed in
// SYSTEM_PARAMS/EXCLUDED_DOMAINS_ADMIN_CENTER. Excluded-domain users are not
// auto-prefilled as owner. All users may leave Account Owners empty (Unassigned).
const isExcludedDomainUser = ref(false)

// Load account owners from Firestore
const loadAccountOwners = async () => {
  loadingAccountOwners.value = true
  try {
    const auth = getAuth()
    const currentUser = auth.currentUser

    if (!currentUser) {
      handleErrors('loadAccountOwners', new Error('Unable to load account owners. Please ensure you are logged in and try again.'))
      return
    }
    const excludedDomains = await getExcludedDomains()
    isExcludedDomainUser.value = isExcludedEmail(currentUser.email || '', excludedDomains)

    const response = await simpleGet(
      'loadAccountOwners',
      `${functionsBaseUrl}/usersGateway/api/v1/users/account-owners`,
      { rethrow: true }
    )
    const users = (response?.data?.data || []).map(owner => ({
      value: owner.email,
      label: owner.fullname
    }))

    accountOwnerOptions.value = users

    // Set default value: existing employer owners or auto-fill with logged user.
    // Only pre-fill when the logged user's domain is NOT excluded.
    const existingOwners = getAccountOwners(props.employerData)
    if (existingOwners.length > 0) {
      accountOwners.value = existingOwners
    } else if (!props.isEditMode && currentUser?.email && !isExcludedDomainUser.value) {
      const match = accountOwnerOptions.value.find(
        (o) => o.value === normalizeOwnerEmail(currentUser.email)
      )
      if (match) {
        accountOwners.value = [match.value]
      }
    }
    // Propagate change upwards
    await updateFatherForm()
  } catch (error) {
    handleErrors('loadAccountOwners', error)
  } finally {
    loadingAccountOwners.value = false
  }
}


// Form validation setup
const { defineField, errors, validate, setValues, isFieldTouched, resetForm } = useForm({
  initialValues: {
    name: props.employerData.name || '',
    accountOwners: getAccountOwners(props.employerData),
    ein: props.employerData.ein || '',
    plan_start_date: props.employerData.plan_start_date || '1',
    contact_name: props.employerData.contact_name || '',
    contact_emails: props.employerData.contact_emails || ''
  },
  validationSchema: toTypedSchema(
    object({
      name: string().min(4).required().max(150),
      accountOwners: array().of(string().email()).max(MAX_ACCOUNT_OWNERS),
      ein: string()
        .required()
        .matches(/^[0-9]{2}-[0-9]{7}$/),
      plan_start_date: date()
        .typeError('Please enter a valid date')
        .required()
        .test('is-date-complete', 'Invalid date format', (value) => {
          if (!plan_start_date.value) return true
          const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/g
          return dateRegex.test(plan_start_date.value)
        })
        .test('is-one-year-after', 'Please enter a plan year start date at least 1 year past the current plan year start date', (value) => {
          if (!value || !props.lastPlanStartDate) return true
          
          // Parse the dates
          const lastDate = new Date(props.lastPlanStartDate)
          const newDate = new Date(value)
          
          // Calculate the difference in years
          const yearDiff = newDate.getFullYear() - lastDate.getFullYear()
          const monthDiff = newDate.getMonth() - lastDate.getMonth()
          const dayDiff = newDate.getDate() - lastDate.getDate()
          
          // Check if at least one year has passed
          if (yearDiff > 1) return true
          if (yearDiff === 1 && (monthDiff > 0 || (monthDiff === 0 && dayDiff >= 0))) return true
          
          return false
        }),
      contact_name: string().required(),
      contact_emails: string()
        .required()
        .max(255, 'Email list cannot exceed 255 characters')
        .test('is-email-list', 'Invalid email list format', (value) => {
          if (!value) return true
          const emails = value.split(',').map((email) => email.trim())
          return emails.every((email) => {
            // eslint-disable-next-line no-useless-escape
            const emailRegex = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,12}$/g
            return emailRegex.test(email.trim())
          })
        })
    })
  )
})

// Define fields with validation
const [name, nameAttrs] = defineField('name')
const [accountOwners] = defineField('accountOwners')
const [ein, einAttrs] = defineField('ein')
const [plan_start_date, planStartDateAttrs] = defineField('plan_start_date')
const [contact_name, contactNameAttrs] = defineField('contact_name')
const [contact_emails, contactEmailsAttrs] = defineField('contact_emails')

const updateFatherForm = async () => {
  const { valid } = await validate()
  const owners = accountOwners.value || []
  emit('update', {
    values: {
      name: name.value,
      accountOwners: owners,
      ein: ein.value,
      plan_start_date: plan_start_date.value,
      contact_name: contact_name.value,
      contact_emails: contact_emails.value
    },
    isValid: valid
  })
}

// Initialize the form when the component is mounted
const initializeForm = async () => {
  setValues({
    name: props.employerData.name || '',
    accountOwners: getAccountOwners(props.employerData),
    ein: props.employerData.ein || '',
    plan_start_date: props.employerData.plan_start_date || '',
    contact_name: props.employerData.contact_name || '',
    contact_emails: props.employerData.contact_emails || ''
  })
  // Emit initial values without validating
  await updateFatherForm()
}

watch(() => props.forceUpdate, async () => {
  // Reset touched and form state before reinitializing.
  // Await both async operations in order to avoid a race condition where
  // loadAccountOwners could pre-fill accountOwner before initializeForm
  // clears it via setValues (leaving the dropdown blank).
  resetForm()
  await initializeForm()
  await loadAccountOwners()
}, { immediate: true })

const openRenewEmployerModalForDuplicatedUser = () => {
    // Emit event to open the modal with the current employer data
    emitter.emit('open-renew-employer-modal', {
      ...(props.duplicateEmployerMatch || {}),
      isFromDuplicateButton: true
    })
  }
</script>

<template>
  <div>
    <!-- Employer Name field (hidden when hideEmployerName is true) -->
    <div class="mb-3">
      <label for="employerName" class="form-label text-14">
        <strong>Employer Name</strong>
      </label>
      <input 
        type="text" 
        class="form-control text-16" 
        id="employerName" 
        v-model="name"
        maxlength="150"
        v-bind="nameAttrs"
        @input="updateFatherForm()"
        @blur="() => { props.isNameTaken() }"
        :class="{ 'is-invalid': isFieldTouched('name') && errors.name }"
        placeholder="e.g., XYZ Company"
      >
      <div v-if="props.checkDuplicateEmployer && isFieldTouched('name')" class="duplicate-name-container mt-1 small">
        <div class="d-flex duplicate-name-container__message">
          <i class="bi bi-info-circle-fill"></i>
           <div v-if="get(props,'duplicateEmployerMatch.is_plan_complete')">
            <BcsDynamicText text="duplicateEmployerText" />
          </div>
          <div v-else>
            <BcsDynamicText text="duplicateEmployerTextNoRenew" />
          </div>
        </div>

        <div class="duplicate-name-container__buttons ">
        
          <div class="d-flex justify-content-between">
            <button class="btn btn-primary text-14" @click="openRenewEmployerModalForDuplicatedUser" v-if="get(props,'duplicateEmployerMatch.is_plan_complete')">
              Create Renewal 
              <i class="bi bi-plus-circle ms-1"></i>
            </button>
            <button data-bs-dismiss="modal" class="btn btn-info text-14" @click="props.goToExistingEmployer()">
              Go to Existing Employer Dashboard
              <i class="bi bi-chevron-right"></i>
            </button>
          </div>

          <div @click="props.continueEditEmployer"  class="text-primary fw-bold continue-adding-button">
            Continue Adding a New Employer
          </div>
          
        </div>
      </div>
    </div>
    
    <div class="d-flex" style="gap: 28px;">
      <div class="mb-3" style="width: 60%;">
        <label for="employerEIN" class="form-label text-14">
          <strong>Employer EIN</strong>
        </label>
        <input 
          type="text" 
          class="form-control text-16" 
          id="employerEIN" 
          v-model="ein"
          v-bind="einAttrs"
          @input="updateFatherForm()"
          :class="{ 'is-invalid': isFieldTouched('ein') && errors.ein }"
          v-maska="rawMaskVal"
          data-maska="##-#######"
          placeholder="e.g., 12-3456789"
        >
      </div>
      
      <div class="mb-3">
        <label for="planStartDate" class="form-label text-14">
          <strong>Plan Start Date</strong>
        </label>
        <input 
          type="text" 
          class="form-control text-16" 
          id="planStartDate" 
          v-model="plan_start_date"
          v-bind="planStartDateAttrs"
          @input="updateFatherForm()"
          :class="{ 'is-invalid': isFieldTouched('plan_start_date') && errors.plan_start_date }"
          v-maska="rawMaskVal"
          data-maska="##/##/####"
          placeholder="e.g., MM/DD/YYYY"
        >
      </div>
    </div>
    
    <div class="mb-3">
      <label for="primaryContactName" class="form-label text-14">
        <strong>Primary Contact Name</strong>
      </label>
      <input 
        type="text" 
        class="form-control text-16" 
        id="primaryContactName" 
        v-model="contact_name"
        v-bind="contactNameAttrs"
        @input="updateFatherForm()"
        :class="{ 'is-invalid': isFieldTouched('contact_name') && errors.contact_name }"
        placeholder="e.g., Jane Doe"
      >
    </div>
    
    <div class="mb-3">
      <label for="primaryContactEmail" class="form-label text-14">
        <strong>Primary Contact Email(s)</strong>
      </label>
      <p class="text-14 text-gray-700 mb-1">
        <BcsDynamicText text="primaryContactEmailsDescriptionText" />
      </p>
      <input 
        type="text" 
        class="form-control text-16" 
        id="primaryContactEmail" 
        v-model="contact_emails"
        v-bind="contactEmailsAttrs"
        @input="updateFatherForm()"
        :class="{ 'is-invalid': isFieldTouched('contact_emails') && errors.contact_emails }"
        placeholder="e.g., janedoe@xyz.com, johnsmith@xyz.com"
      >
    </div>

    <!-- Account Owners field (up to 3) -->
    <div class="mb-3">
      <label for="accountOwner" class="form-label text-14">
        <strong>Account Owner(s)</strong>
      </label>
      <p class="text-14 text-gray-700 mb-1">Select up to three account owners.</p>
      <BcsDropdownSelect
        id="accountOwner"
        :model-value="accountOwners || []"
        @update:model-value="accountOwners = $event; updateFatherForm()"
        :options="accountOwnerOptions"
        :multi-select="true"
        :max-selections="MAX_ACCOUNT_OWNERS"
        :placeholder="loadingAccountOwners ? 'Loading...' : 'Unassigned'"
        :disabled="loadingAccountOwners"
        :is-invalid="isFieldTouched('accountOwners') && !!errors.accountOwners"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
  .duplicate-name-container{
    color: #521B98;

    &__message{
      gap: 12px;
    }

    &__buttons{
      margin-top: 20px;
      padding-left: 24px;
    }

    .continue-adding-button{
      margin-top: 14px;
      cursor: pointer;
    }
  }

</style>
